
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import en from '../translations/en';
import zh from '../translations/zh';

// Flatten the nested translations object to a flat structure
const flattenTranslations = (obj: any, prefix: string = ''): Record<string, string> => {
  return Object.keys(obj).reduce((acc: Record<string, string>, key: string) => {
    const prefixKey = prefix ? `${prefix}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(acc, flattenTranslations(obj[key], prefixKey));
    } else {
      acc[prefixKey] = obj[key];
    }
    return acc;
  }, {});
};

// Flatten both translation files
const flatEn = flattenTranslations(en);
const flatZh = flattenTranslations(zh);

interface LanguageContextType {
  language: 'en' | 'zh';
  setLanguage: (lang: 'en' | 'zh') => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<'en' | 'zh'>('en');
  const [translations, setTranslations] = useState<Record<string, string>>(flatEn);

  useEffect(() => {
    // Get saved language preference
    const savedLanguage = localStorage.getItem('beam-language');
    
    if (savedLanguage === 'zh') {
      setLanguage('zh');
      setTranslations(flatZh);
    } else {
      setLanguage('en');
      setTranslations(flatEn);
    }
  }, []);

  const handleSetLanguage = (lang: 'en' | 'zh') => {
    setLanguage(lang);
    localStorage.setItem('beam-language', lang);
    
    if (lang === 'zh') {
      setTranslations(flatZh);
    } else {
      setTranslations(flatEn);
    }

    console.log('Language changed to:', lang);
  };

  // Translation function that handles nested keys
  const t = (key: string): string => {
    const result = translations[key] || key;
    
    // For development and debugging
    if (result === key && process.env.NODE_ENV === 'development') {
      console.log(`Translation missing for key: ${key}`);
    }
    
    return result;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
